import React, { useEffect, useState } from 'react';
import { Sparkles, X, Loader2, ChevronRight, TrendingUp, Heart, Trophy, AlertTriangle, FileDown } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../context/AppContext';
import { toast } from 'sonner';
import TransformationStoryCarousel from './TransformationStoryCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * CycleReassessmentModal — J42 reassessment wizard (Iter. 405.j).
 *
 * Triggered when a 6-week cycle ends. Walks the user through:
 *   Step 1: Auto recap (weight trend, posture, workouts done) — read-only.
 *   Step 2: Next goal + current pain zones + energy baseline.
 *   Step 3: Health / chronic conditions / medications (free-form chips).
 *   Step 4: Loading → new plan ready → CTA to /workouts.
 *
 * The backend's `POST /training-cycles/{cycle_id}/reassess` compiles the
 * bilan server-side, builds the AI coach prompt, calls Gemini, persists
 * the new plan, and opens the next 6-week cycle. We just collect the
 * 3 user-stated answer blocks.
 */
const STEPS = ['recap', 'goals', 'health', 'choice', 'generating'];

const GOAL_OPTIONS = [
  { key: 'fat_loss', label: { fr: 'Perte de poids', en: 'Fat loss' }, Icon: TrendingUp },
  { key: 'muscle_gain', label: { fr: 'Prise de masse', en: 'Muscle gain' }, Icon: Trophy },
  { key: 'maintenance', label: { fr: 'Maintien / Forme', en: 'Maintenance / Fitness' }, Icon: Heart },
];

const PAIN_ZONES = ['genoux', 'dos', 'epaule', 'poignet', 'cheville', 'nuque'];
const ENERGY_OPTIONS = [
  { key: 'high', label: { fr: 'Énergie au top', en: 'High energy' } },
  { key: 'normal', label: { fr: 'Normale', en: 'Normal' } },
  { key: 'low', label: { fr: 'Faible / Fatigué', en: 'Low / Tired' } },
];

export default function CycleReassessmentModal({ open, onClose, cycle, language = 'fr', onSuccess }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [bilan, setBilan] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [transformationPairs, setTransformationPairs] = useState({});
  const [answers, setAnswers] = useState({
    goal_next: null,
    pain_zones: [],
    energy_baseline: 'normal',
    current_injuries: [],
    // Iter. 406.x — Verbatim free-text injury/pain/limitation description.
    // Sent as the PRIMARY injury signal to the AI generator.
    injuries_description: '',
    chronic_conditions: '',
    medications_affecting_training: '',
    sleep_quality_avg: 7,
    stress_level: 5,
    // Iter. 406.o — Renewal strategy choice (Option A vs Option B)
    renewal_strategy: 'regenerate',
  });

  const fr = language === 'fr';

  useEffect(() => {
    if (!open || !cycle) return;
    // The bilan is computed by the backend when reassess() is called;
    // for the recap step we just show what we already know on the cycle
    // doc (or could fetch a preview endpoint later).
    setBilan({
      cycle_number: cycle.cycle_number,
      duration_days: cycle.duration_days,
    });
    setStepIdx(0);
    // Iter. 406.i — fetch before/after photo pairs for the carousel.
    // Fire-and-forget — failure simply hides the section, never blocks the modal.
    setTransformationPairs({});
    api.get(`/training-cycles/${cycle.cycle_id}/transformation-pairs`)
      .then((res) => setTransformationPairs(res.data?.pairs || {}))
      .catch(() => { /* missing pairs — show empty state */ });
  }, [open, cycle]);

  if (!cycle) return null;

  const handleClose = () => {
    if (stepIdx === 4) return; // never close mid-generation
    onClose();
  };

  const canAdvance = () => {
    const step = STEPS[stepIdx];
    if (step === 'recap') return true;
    if (step === 'goals') return !!answers.goal_next;
    if (step === 'health') return true;
    if (step === 'choice') return answers.renewal_strategy === 'regenerate' || answers.renewal_strategy === 'keep';
    return false;
  };

  const submit = async () => {
    setStepIdx(4);
    try {
      const payload = {
        goal_next: answers.goal_next,
        pain_zones: answers.pain_zones,
        energy_baseline: answers.energy_baseline,
        // Iter. 406.x — Send the verbatim free-text description as the
        // PRIMARY injury input. The legacy `current_injuries` array is also
        // populated (single-element wrapper) so older backend code paths
        // and the bilan snapshot continue to work without changes.
        injuries_description: (answers.injuries_description || '').trim() || null,
        current_injuries: (answers.injuries_description || '').trim()
          ? [(answers.injuries_description || '').trim()]
          : [],
        chronic_conditions: (answers.chronic_conditions || '')
          .split(',').map((s) => s.trim()).filter(Boolean),
        medications_affecting_training: (answers.medications_affecting_training || '')
          .split(',').map((s) => s.trim()).filter(Boolean),
        sleep_quality_avg: Number(answers.sleep_quality_avg) || null,
        stress_level: Number(answers.stress_level) || null,
        // Iter. 406.o — Option A "regenerate" or Option B "keep"
        renewal_strategy: answers.renewal_strategy || 'regenerate',
      };
      const res = await api.post(`/training-cycles/${cycle.cycle_id}/reassess`, payload);
      const strat = res.data?.strategy;
      toast.success(fr
        ? (strat === 'keep'
            ? 'Plan conserve, nouveau cycle de 6 semaines lance !'
            : 'Ton prochain bloc est pret !')
        : (strat === 'keep'
            ? 'Plan kept, new 6-week cycle started!'
            : 'Your next block is ready!'));
      if (onSuccess) onSuccess(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || (fr ? 'Echec de la generation. Reessaie.' : 'Generation failed. Try again.');
      toast.error(detail);
      setStepIdx(3);
    }
  };

  const next = () => {
    if (!canAdvance()) return;
    if (stepIdx === 3) submit();
    else setStepIdx((s) => s + 1);
  };

  const togglePain = (k) => setAnswers((a) => ({
    ...a,
    pain_zones: a.pain_zones.includes(k) ? a.pain_zones.filter((x) => x !== k) : [...a.pain_zones, k],
  }));

  const downloadPdfReport = async () => {
    if (!cycle?.cycle_id) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/training-cycles/${cycle.cycle_id}/report`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `yumix-bilan-cycle-${cycle.cycle_number || ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(fr ? 'Rapport PDF telecharge !' : 'PDF report downloaded!');
    } catch (err) {
      toast.error(fr ? 'Echec du telechargement du PDF' : 'PDF download failed');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="bg-card border-border max-w-md p-0 overflow-hidden" data-testid="cycle-reassessment-modal">
        <div className="h-1 bg-secondary/50 w-full">
          <div
            className="h-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 transition-all duration-300"
            style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              {fr ? `Cycle ${cycle.cycle_number} • Bilan IA` : `Cycle ${cycle.cycle_number} • AI Review`}
            </span>
            {stepIdx !== 3 && (
              <button onClick={handleClose} className="p-1 rounded-full hover:bg-secondary" data-testid="cycle-reassess-close">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {STEPS[stepIdx] === 'recap' && (
            <div className="space-y-3 min-h-[260px]">
              <h2 className="text-xl font-heading uppercase tracking-tight">
                {fr ? 'Bravo !' : 'Well done!'}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {fr
                  ? `Tu viens de boucler 6 semaines de training. Avant de te proposer ton prochain bloc, Coach YUMIX va analyser tes progres et ton etat actuel.`
                  : `You just completed 6 weeks of training. Before generating your next block, Coach YUMIX will analyze your progress and current state.`}
              </p>
              <div className="rounded-xl bg-secondary/40 border border-border p-3 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">{fr ? 'Cycle' : 'Cycle'}</span><span className="font-bold">{cycle.cycle_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{fr ? 'Duree' : 'Duration'}</span><span className="font-bold">{cycle.duration_days} {fr ? 'jours' : 'days'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{fr ? 'Demarre le' : 'Started'}</span><span className="font-bold">{cycle.start_date?.slice(0, 10)}</span></div>
              </div>
              <p className="text-xs text-violet-400 mt-2">
                {fr ? 'On va te poser 2 series de questions tres courtes. Sois honnete — l\'IA s\'adapte a ta realite.' : "We'll ask 2 quick sets of questions. Be honest — the AI adapts to YOUR reality."}
              </p>

              {/* Iter. 406.i — Transformation story carousel (avant J7 vs apres cycle 6 sem.) */}
              <div className="mt-4 pt-4 border-t border-violet-500/20">
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-2 text-center">
                  {fr ? 'Ta transformation visuelle' : 'Your visual transformation'}
                </p>
                <TransformationStoryCarousel
                  pairs={transformationPairs}
                  language={language}
                  backendUrl={BACKEND_URL}
                />
              </div>

              <button
                type="button"
                onClick={downloadPdfReport}
                disabled={downloadingPdf}
                data-testid="cycle-reassess-download-pdf"
                className="w-full mt-2 px-3 py-2.5 rounded-xl border-2 border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 transition flex items-center justify-center gap-2 text-sm font-semibold text-violet-300 disabled:opacity-60"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                {fr ? 'Telecharger mon rapport EXTRA MILES (PDF)' : 'Download my EXTRA MILES report (PDF)'}
              </button>
            </div>
          )}

          {STEPS[stepIdx] === 'goals' && (
            <div className="space-y-4 min-h-[260px]">
              <h2 className="text-lg font-heading uppercase">{fr ? 'Ton objectif pour les 6 prochaines semaines ?' : 'Your goal for the next 6 weeks?'}</h2>
              <div className="space-y-2">
                {GOAL_OPTIONS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => setAnswers((a) => ({ ...a, goal_next: key }))}
                    data-testid={`cycle-reassess-goal-${key}`}
                    className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 text-left transition ${
                      answers.goal_next === key ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <Icon className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold">{label[language] || label.fr}</span>
                  </button>
                ))}
              </div>
              <div className="pt-3 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{fr ? 'Douleurs en ce moment ?' : 'Pain right now?'}</p>
                <div className="flex flex-wrap gap-1.5">
                  {PAIN_ZONES.map((k) => {
                    const sel = answers.pain_zones.includes(k);
                    return (
                      <button
                        key={k}
                        onClick={() => togglePain(k)}
                        data-testid={`cycle-reassess-pain-${k}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${sel ? 'border-primary bg-primary/15' : 'border-border'}`}
                      >
                        {k}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{fr ? 'Energie de base aujourd\'hui' : 'Today\'s baseline energy'}</p>
                <div className="grid grid-cols-3 gap-2">
                  {ENERGY_OPTIONS.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setAnswers((a) => ({ ...a, energy_baseline: key }))}
                      data-testid={`cycle-reassess-energy-${key}`}
                      className={`py-2 rounded-xl text-xs font-semibold border ${answers.energy_baseline === key ? 'border-primary bg-primary/10' : 'border-border'}`}
                    >
                      {label[language] || label.fr}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {STEPS[stepIdx] === 'health' && (
            <div className="space-y-3 min-h-[260px]">
              <h2 className="text-lg font-heading uppercase flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                {fr ? 'Sante / blessures' : 'Health / Injuries'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {fr
                  ? 'Coach YUMIX adaptera tes exercices a ces conditions. Sois precis.'
                  : 'Coach YUMIX will adapt your exercises to these conditions. Be specific.'}
              </p>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  {fr
                    ? 'Avez-vous des blessures, des douleurs ou des limitations physiques ?'
                    : 'Any injuries, pain or physical limitations?'}
                </label>
                {/* Iter. 406.x — Free-text injury textarea (was a comma-split
                    Input). Now matches the Coach interface precision and is
                    consumed verbatim by ai_workout_generator.py as the
                    PRIMARY injury signal. */}
                <textarea
                  value={answers.injuries_description || ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, injuries_description: e.target.value }))}
                  placeholder={fr
                    ? 'Ex: Hernie discale lombaire, tendinite à l\'épaule droite, douleur aux genoux en faisant des squats, etc. (Laissez vide si aucune).'
                    : 'E.g. lumbar disc herniation, right shoulder tendinitis, knee pain on squats, etc. (Leave empty if none).'}
                  rows={4}
                  maxLength={1200}
                  className="w-full rounded-lg bg-secondary border border-border text-sm leading-relaxed px-3 py-2 focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                  data-testid="cycle-reassess-injuries"
                />
                <div className="mt-1 text-[10px] text-muted-foreground text-right tabular-nums">
                  {(answers.injuries_description || '').length}/1200
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  {fr ? 'Pathologies chroniques' : 'Chronic conditions'}
                </label>
                <Input
                  value={answers.chronic_conditions}
                  onChange={(e) => setAnswers((a) => ({ ...a, chronic_conditions: e.target.value }))}
                  placeholder={fr ? 'Ex: hypertension, diabete type 2' : 'e.g. hypertension, type 2 diabetes'}
                  className="bg-secondary border-border text-sm"
                  data-testid="cycle-reassess-chronic"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  {fr ? 'Medications a signaler' : 'Medications to flag'}
                </label>
                <Input
                  value={answers.medications_affecting_training}
                  onChange={(e) => setAnswers((a) => ({ ...a, medications_affecting_training: e.target.value }))}
                  placeholder={fr ? 'Ex: beta-bloquant, anticoagulant' : 'e.g. beta-blocker, anticoagulant'}
                  className="bg-secondary border-border text-sm"
                  data-testid="cycle-reassess-meds"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    {fr ? 'Sommeil (1-10)' : 'Sleep (1-10)'}
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={answers.sleep_quality_avg}
                    onChange={(e) => setAnswers((a) => ({ ...a, sleep_quality_avg: e.target.value }))}
                    className="bg-secondary border-border"
                    data-testid="cycle-reassess-sleep"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    {fr ? 'Stress (1-10)' : 'Stress (1-10)'}
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={answers.stress_level}
                    onChange={(e) => setAnswers((a) => ({ ...a, stress_level: e.target.value }))}
                    className="bg-secondary border-border"
                    data-testid="cycle-reassess-stress"
                  />
                </div>
              </div>
            </div>
          )}

          {STEPS[stepIdx] === 'generating' && (
            <div className="flex flex-col items-center justify-center min-h-[260px] text-center space-y-3">
              <Loader2 className="w-10 h-10 animate-spin text-violet-400" />
              <h2 className="text-lg font-heading uppercase">
                {answers.renewal_strategy === 'keep'
                  ? (fr ? 'Conservation du plan en cours...' : 'Keeping your plan...')
                  : (fr ? 'Coach YUMIX prepare ton prochain bloc...' : 'Coach YUMIX is crafting your next block...')}
              </h2>
              <p className="text-xs text-muted-foreground max-w-xs">
                {answers.renewal_strategy === 'keep'
                  ? (fr
                      ? 'Reinitialisation du compteur 7-jours et ouverture d\'un nouveau cycle de 6 semaines.'
                      : 'Resetting the 7-day quest counter and opening a new 6-week cycle.')
                  : (fr
                      ? 'Il analyse tes 42 jours : poids, posture, energie, blessures. Donne-lui 10-15 secondes.'
                      : 'Analyzing your 42 days: weight, posture, energy, injuries. Give it 10-15 seconds.')}
              </p>
            </div>
          )}

          {/* Iter. 406.o — Option A / Option B renewal choice */}
          {STEPS[stepIdx] === 'choice' && (
            <div className="space-y-4 min-h-[260px]" data-testid="cycle-reassess-choice-step">
              <h2 className="text-lg font-heading uppercase">
                {fr ? 'Comment tu veux continuer ?' : 'How do you want to continue?'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {fr
                  ? 'Choisis la suite de ton aventure : un nouveau plan dicte par l\'IA ou la continuite de celui que tu connais deja.'
                  : 'Pick the path forward: a fresh AI-built plan or keep the one you already know.'}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  {
                    key: 'regenerate',
                    badge: 'OPTION A',
                    title: fr ? 'Nouveau plan IA' : 'Fresh AI plan',
                    desc: fr
                      ? 'Generer un nouveau bloc 6 semaines adapte a ma progression.'
                      : 'Generate a new 6-week block tailored to my progress.',
                    icon: '🔥',
                    accent: 'border-orange-400/60 bg-orange-500/15',
                  },
                  {
                    key: 'keep',
                    badge: 'OPTION B',
                    title: fr ? 'Conserver mon plan' : 'Keep my plan',
                    desc: fr
                      ? 'Garder mon plan actuel + reinitialiser les Defis 7 jours pour 6 nouvelles semaines.'
                      : 'Keep my current plan + reset the 7-day quests for a fresh 6 weeks.',
                    icon: '🛡️',
                    accent: 'border-emerald-400/60 bg-emerald-500/15',
                  },
                ].map((opt) => {
                  const selected = answers.renewal_strategy === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, renewal_strategy: opt.key }))}
                      data-testid={`cycle-reassess-strategy-${opt.key}`}
                      className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                        selected
                          ? `${opt.accent} ring-2 ring-white/30`
                          : 'border-border bg-secondary/40 hover:bg-secondary/70'
                      }`}
                    >
                      <span className="absolute top-2 right-3 text-[9px] uppercase tracking-widest font-black text-white/60">
                        {opt.badge}
                      </span>
                      <div className="text-3xl mb-1.5">{opt.icon}</div>
                      <p className="font-heading uppercase text-sm text-white leading-tight mb-1">
                        {opt.title}
                      </p>
                      <p className="text-[11px] text-white/70 leading-snug">
                        {opt.desc}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stepIdx !== 4 && (
            <div className="mt-5 flex gap-2">
              <Button
                onClick={next}
                disabled={!canAdvance()}
                className="flex-1 bg-primary text-primary-foreground"
                data-testid="cycle-reassess-next"
              >
                {stepIdx === 3
                  ? (answers.renewal_strategy === 'keep'
                      ? (fr ? 'Conserver et relancer' : 'Keep & restart')
                      : (fr ? 'Générer mon nouveau bloc' : 'Generate my next block'))
                  : (fr ? 'Continuer' : 'Continue')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
